import * as React from 'react';
import { useMemo, useState, useCallback } from 'react';
import * as Immutable from 'immutable';
import styled from 'styled-components';

import SectionComponent from 'components/common/Section/SectionComponent';
import { Table } from 'components/bootstrap';
import { PaginatedItemOverview, Spinner } from 'components/common';
import type { Sort } from 'stores/PaginationTypes';
import { fetchPaginatedMembershipPolicies } from 'collections/hooks/useMembershipPolicies';
import type {
  DescriptiveItem,
  OverrideItemComponentProps,
  ResultsWrapperComponentProps,
} from 'components/common/PaginatedItemOverview';
import { DEFAULT_PAGINATION } from 'components/common/PaginatedItemOverview';
import EntityCollectionItem from 'collections/components/CollectionMembershipConfig/EntityCollectionItem';
import useMembershipPoliciesMutations from 'collections/hooks/useMembershipPoliciesMutations';
import CollectionsSelector from 'collections/components/CollectionMembershipConfig/CollectionsSelector';
import UserNotification from 'util/UserNotification';
import useLicenseValidityForSubject from 'license/hooks/useLicenseValidityForSubject';
import { LICENSE_SUBJECTS } from 'license/constants';
import { COLLECTIONS_ENABLED } from 'collections/constants';

type Props = {
  entityId: string;
  entityType: 'team';
  readOnly?: boolean;
};

const CollectionRenderer = ({ item, onDeleteItem }: OverrideItemComponentProps) => (
  <EntityCollectionItem item={item} onDeleteItem={onDeleteItem ?? undefined} />
);

const getCollectionWrapper =
  (shouldShowActions: boolean = false) =>
  ({ isEmptyResult, children }: ResultsWrapperComponentProps) =>
    isEmptyResult ? (
      <div>{children}</div>
    ) : (
      <Table condensed striped>
        <thead>
          <tr>
            <th>Collection Name</th>
            <th>Description</th>
            {shouldShowActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
    );

const Container = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const CollectionMembershipConfigSection = ({ entityId, entityType, readOnly = false }: Props) => {
  const [loading, setLoading] = useState(false);
  const [paginatedMembershipPolicies, setPaginatedMembershipPolicies] = useState<any | undefined>();
  const { removeMembershipPolicy, createMembershipPolicy } = useMembershipPoliciesMutations();
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense,
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);

  const searchParams = useMemo(
    () => ({
      page: 1,
      pageSize: 10,
      query: `initiator_id:${entityId}`,
      sort: { attributeId: 'initiator_id', direction: 'asc' } as Sort,
    }),
    [entityId],
  );

  const onLoad = useCallback(
    (pagination = DEFAULT_PAGINATION) => {
      setLoading(true);

      return fetchPaginatedMembershipPolicies({
        ...searchParams,
        pageSize: pagination.perPage,
        page: pagination.page,
      }).then((paginatedMembershipPoliciesResult) => {
        setLoading(false);
        const { list, pagination: responsePagination } = paginatedMembershipPoliciesResult;
        const policiesItems = Immutable.List<DescriptiveItem>(
          list.map((policy) => ({ id: policy.id, name: policy.target_id, description: policy.target_type })),
        );

        return {
          list: policiesItems,
          pagination: responsePagination,
        };
      });
    },
    [searchParams],
  );
  const reload = () => onLoad().then(setPaginatedMembershipPolicies);

  const handleDelete = (item: DescriptiveItem) => {
    removeMembershipPolicy({ id: item.id }).then(() => {
      reload();
    });
  };
  const handleSubmit = (collectionIds: Array<string>) => {
    const requests = collectionIds.map((id) =>
      createMembershipPolicy({
        entity: {
          initiator_type: entityType,
          initiator_id: entityId,
          target_type: 'collection',
          target_id: id,
        } as any,
      }),
    );

    Promise.allSettled(requests)
      .then(() => {
        UserNotification.success('Collection membership policies has been successfully updated.', 'Success');
        reload();
      })
      .catch(() =>
        UserNotification.error('There was an error updating one or many collection membership policies.', 'Success'),
      );
  };

  if (!COLLECTIONS_ENABLED) return null;

  if (isLoadingLicense) {
    return <Spinner />;
  }

  return (
    isValidLicense && (
      <SectionComponent
        title="Default collections membership for entities created by team member"
        showLoading={loading}>
        <Container>
          {!readOnly && loading && <Spinner />}
          {!readOnly && <CollectionsSelector entityId={entityId} onSubmit={handleSubmit} entityType={entityType} />}
          <PaginatedItemOverview
            noDataText="No selected collections have been found."
            onLoad={onLoad}
            disableSearchFilter
            resultsWrapperComponent={getCollectionWrapper()}
            overrideItemComponent={CollectionRenderer}
            overrideList={paginatedMembershipPolicies}
            onDeleteItem={!readOnly ? handleDelete : null}
          />
        </Container>
      </SectionComponent>
    )
  );
};

export default CollectionMembershipConfigSection;
